'use client';

import {Fragment, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {AboutFeatureItemsSection, AboutMediaBannerSection, AboutOverviewSection, AboutOverviewStatisticsSection} from '../components/about';
import {BannerCard} from '../components/banner-card';
import {BrandQuoteSection, type BrandHeadlineVariant, type BrandQuoteCta} from '../components/brand';
import {ScreenSectionType, mediaFromWire, type DetailedScreenSection, type GrovaAboutContent} from '../model';

export interface AboutPageProps {
  content: GrovaAboutContent;
  quoteCta: BrandQuoteCta;
  headlineVariant?: BrandHeadlineVariant;
  bannerScrim?: boolean;
}

function renderSection(section: DetailedScreenSection, quoteCta: BrandQuoteCta, headlineVariant?: BrandHeadlineVariant): ReactNode {
  switch (section.type) {
    case ScreenSectionType.Overview:
      return <AboutOverviewSection title={section.name ?? ''} overview={section.overview} sectionId={section.uuid} />;
    case ScreenSectionType.OverviewAndStatistics:
      return <AboutOverviewStatisticsSection title={section.name ?? ''} overview={section.overview} sectionId={section.uuid} />;
    case ScreenSectionType.MediaBanner:
      return <AboutMediaBannerSection mediaBanner={section.mediaBanner} sectionId={section.uuid} />;
    case ScreenSectionType.FeatureItems:
      return <AboutFeatureItemsSection title={section.name ?? ''} featureItems={section.featureItems} sectionId={section.uuid} />;
    case ScreenSectionType.Quote: {
      const media = mediaFromWire(section.quote?.media);
      if (!media || !section.name) {
        return null;
      }
      return <BrandQuoteSection title={section.name} variant={headlineVariant} image={media.src} cta={quoteCta} />;
    }
    case ScreenSectionType.Hero:
    case ScreenSectionType.Spotlight:
    case ScreenSectionType.FeaturedProjects:
    case ScreenSectionType.Countries:
    case ScreenSectionType.AboutTeaser:
    case ScreenSectionType.LatestNews:
      return null;
  }
}

export function AboutPage({content, quoteCta, headlineVariant, bannerScrim}: AboutPageProps) {
  const {t} = useTranslation();

  return (
    <>
      <BannerCard title={t('about_brand')} imageSrc={content.banner.src} imageAlt={content.bannerTitle} scrim={bannerScrim} />
      {content.sections.map((section, index) => (
        <Fragment key={`${section.type}-${index}`}>{renderSection(section, quoteCta, headlineVariant)}</Fragment>
      ))}
    </>
  );
}

AboutPage.displayName = 'AboutPage';
